// oxlint-disable max-lines-per-function, complexity
import { merge } from 'lodash-es';
import { assertEvent, assign, sendParent } from 'xstate';

import type { JobStartEvent } from '../../operations/types';
import { assertJobDone } from '../../utils';

import { machine } from './actor';

/**
 * Provides the control machine's action and guard implementations, referenced by the state
 * chart in `./actor`. Kept separate from the chart so that external dependencies can be injected
 * into the implementation here.
 */
export const createLogic = () =>
	machine.provide({
		actions: {
			onSubmit: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'submit');

					return merge({}, context.draft, event.draft);
				},
			}),
			onPatch: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'patch');

					return merge({}, context.draft, event.draft);
				},
			}),
			applyScopeLocation: assign({
				draft: ({ context, event }) => {
					assertJobDone(event, 'inferScopeLocation');

					// keep the scope and place breakdown (CHANGE-97720, CHANGE-98274); drop confidences and reasoning
					const { output } = event;
					const inferredScopeLocation = output && {
						scope: output.scope,
						location: output.location.name,
						city: output.location.city,
						adminArea: output.location.adminArea,
						countryCode: output.location.countryCode,
					};

					return merge({}, context.draft, { metadata: { inferredScopeLocation } });
				},
			}),
			// runs after applyScopeLocation, so the draft already holds the location it was inferred from
			requestDecisionMakers: sendParent(({ context }): JobStartEvent => {
				const inferred = context.draft?.metadata?.inferredScopeLocation;
				return {
					type: 'job.start',
					name: 'inferDecisionMakers',
					input: {
						title: context.draft?.input?.issuePrompt ?? '',
						scope: inferred?.scope ?? null,
						location: {
							city: inferred?.city ?? null,
							adminArea: inferred?.adminArea ?? null,
							countryCode: inferred?.countryCode ?? null,
						},
						draftId: context.draft?.id,
					},
				};
			}),
			applyDecisionMakers: assign({
				draft: ({ context, event }) => {
					assertJobDone(event, 'inferDecisionMakers');

					// keep what attach-at-publish needs (CHANGE-99392); `null` when it settled without a result
					const inferredDecisionMakers =
						event.output?.recommendations.map((recommendation) => ({
							canonicalPetitionTargetId: recommendation.canonicalPetitionTargetId,
							politicianId: recommendation.politicianId,
							name: recommendation.name,
							position: recommendation.position,
						})) ?? null;

					return merge({}, context.draft, { metadata: { inferredDecisionMakers } });
				},
			}),
			onError: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'error');

					const currentDraft = context.draft;

					const { issuePrompt = '', personalStory = '' } = currentDraft?.input ?? {};
					const description = `${issuePrompt} <br /><br />${personalStory}`;

					const userDraft = {
						type: 'user_draft',
						petitionDraft: { ask: null, description, image: null },
						recommendedDraft: null,
						editingDraft: null,
						metadata: { imageJobId: null },
					};

					const updatedDraft = merge({}, context.draft, userDraft);

					return updatedDraft;
				},
			}),
			unsetDraftType: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'back');

					const targetBranch = event.target; // target = null

					const updatedDraft = (() => {
						if (targetBranch === null) return merge({}, context.draft, { type: null });

						return context.draft;
					})();

					return updatedDraft;
				},
			}),
			persistDraftType: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'submit');

					const baseDraft = merge({}, context.draft, event.draft);

					const targetBranch = event.target; // 'ai' | 'user'

					const updatedDraft = (() => {
						if (targetBranch === null) return merge({}, baseDraft, { type: null });
						if (targetBranch === 'ai') return merge({}, baseDraft, { type: 'ai_draft' });
						if (targetBranch === 'user') return merge({}, baseDraft, { type: 'user_draft' });
						return baseDraft;
					})();

					return updatedDraft;
				},
			}),
		},
		guards: {
			isJobDone: ({ event }, { name }) => event.type === 'job.done' && event.name === name,
			transitionTo: ({ event }, params) => {
				assertEvent(event, 'submit');

				return event.target === params.target;
			},
			// -- branching and transition point
			isUserDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'user';
				}
				return context.draft?.type === 'user_draft';
			},
			isAiDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'ai';
				}
				return context.draft?.type === 'ai_draft';
			},
		},
	});
